import { useEffect, useState } from 'react';

import { BeforeInstallPromptEvent } from '@/constants';

/**
 * Captures the browser's `beforeinstallprompt` event so the install UI can
 * trigger it later, on a real user gesture, instead of letting the browser
 * show its own mini-infobar.
 *
 * This used to live inline in `app/app.tsx`, inside an effect that also did
 * four unrelated things (app-mode storage, content protection, alert-timeout
 * cleanup) and registered the listener with **no cleanup at all**. That effect
 * re-runs whenever `contentProtection` changes, so each run added another
 * listener that was never removed.
 *
 * @returns whether the browser supports the prompt at all, and the captured
 * event once it has fired (`undefined` until then, and on browsers that never
 * fire it -- iOS Safari, notably, which is why the install UI has its own
 * `isSafari()` path).
 */
export function useInstallPrompt(): InstallPrompt {
	// Read once: this is a static capability of the browser, not something
	// that changes during a session.
	const [isSupportInstallPrompt] = useState(
		() => typeof window.onbeforeinstallprompt === 'object',
	);
	const [deferredPrompt, setDeferredPrompt] = useState<
		BeforeInstallPromptEvent | undefined
	>(undefined);

	useEffect(() => {
		if (!isSupportInstallPrompt) {
			return;
		}

		const onBeforeInstallPrompt = (e: Event) => {
			// Prevents the default mini-infobar or install dialog from
			// appearing on mobile -- the app shows its own affordance and
			// calls `deferredPrompt.prompt()` from there.
			e.preventDefault();
			const promptEvent = e as BeforeInstallPromptEvent;
			setDeferredPrompt(promptEvent);
			// An event can only be prompted once, and `userChoice` settles
			// when the reader answers that prompt -- drop the spent event
			// then. Kept, a second tap on Install called `prompt()` again and
			// rejected. The browser fires a fresh event if installing is
			// still possible later.
			promptEvent.userChoice.then(
				() =>
					setDeferredPrompt((current) =>
						current === promptEvent ? undefined : current,
					),
				() => {},
			);
		};

		// Installed some other way (the browser's own menu, another tab):
		// there's nothing left to prompt for.
		const onAppInstalled = () => {
			setDeferredPrompt(undefined);
		};

		window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
		window.addEventListener('appinstalled', onAppInstalled);
		return () => {
			window.removeEventListener(
				'beforeinstallprompt',
				onBeforeInstallPrompt,
			);
			window.removeEventListener('appinstalled', onAppInstalled);
		};
	}, [isSupportInstallPrompt]);

	return { isSupportInstallPrompt, deferredPrompt };
}

export interface InstallPrompt {
	isSupportInstallPrompt: boolean;
	deferredPrompt?: BeforeInstallPromptEvent;
}
